import { useState, useEffect } from "react";
import type { Profile } from "../../lib/api";
import { AvatarUpload } from "./AvatarUpload";

interface ProfileFormProps {
  profile: Profile | null;
  isLoading: boolean;
  onSave: (data: { name?: string; bio?: string }) => Promise<Profile | undefined>;
  onUploadAvatar: (file: File) => Promise<string | undefined>;
  onDeleteAvatar: () => Promise<void>;
}

export function ProfileForm({
  profile,
  isLoading,
  onSave,
  onUploadAvatar,
  onDeleteAvatar,
}: ProfileFormProps) {
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync form with profile data
  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      setBio(profile.bio || "");
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      await onSave({ name, bio });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    profile && (
      name !== (profile.name || "") ||
      bio !== (profile.bio || "")
    );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {saveSuccess && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          Profile saved successfully!
        </div>
      )}

      <div>
        <AvatarUpload
          currentUrl={
            profile?.avatarUrl
              ? `${profile.avatarUrl}?t=${new Date(profile.updatedAt).getTime()}`
              : null
          }
          onUpload={onUploadAvatar}
          onDelete={onDeleteAvatar}
          isLoading={isLoading}
        />
      </div>

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors"
          placeholder="Your name"
        />
      </div>

      <div>
        <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
          About you
        </label>
        <p className="text-sm text-gray-500 mb-2">
          This helps personalize your AI coaching experience. Share your running experience, goals, or any relevant info.
        </p>
        <textarea
          id="bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={10}
          maxLength={500}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors resize-none"
          placeholder="e.g., I've been running for 2 years, training for my first marathon. I usually run 4x per week and struggle with pacing on long runs..."
        />
        <p className="text-sm text-gray-400 mt-1 text-right">
          {bio.length}/500
        </p>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={isSaving || isLoading || !hasChanges}
          className="px-4 py-2 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {isSaving && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          {isSaving ? "Saving..." : "Save changes"}
        </button>
        {hasChanges && !isSaving && (
          <span className="text-sm text-gray-500">You have unsaved changes</span>
        )}
      </div>
    </form>
  );
}
